import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/auth';
import { CustomerdatabaseService } from './customerdatabase.service';
import { OrganigramApiService } from './organigram-api.service';
import { webhookReceiverBase } from '../mona-app/workflow-builder/org-trigger-editor/org-trigger-editor.model';
import {
  ORGANIGRAM_ROLE_EFFECTIVE,
  PermissionsLoad,
  PermissionsRoles,
  PermissionsStatus,
  UNKNOWN_PERMISSIONS,
  customerSafePermissions,
  isFullAccessCustomerEmail,
  previewPermissions,
  withDefaultViewer,
} from '../mona-app/workflow-graph/logic/organigram-permission';
import { isMonaAdminEmail } from '../admin/pricing/pricing-model';

// The role-based-access API, deployed beside the customer's data in THEIR project - same host as
// organigramCreateWebhook and organigramGetDownloadUrl, not the organigram REST backend.
const ACCESS_FN = 'accessApi';

// This deployment is the "enterprise" layout: one account, and its id is the literal string 'default'.
// Named rather than inlined so the day it becomes a real account id is one edit, not a grep. Note this
// is the ACCOUNT id - unrelated to the organigram id, where a literal 'default' is banned outright
// (see OrganigramApiService: it used to load whatever organigram sat under that name).
const ACCOUNT_SEGMENT = 'default';

// How long settled() holds a read for the first answer. Past it the answer counts as unknown, which is
// the same as a 5xx: the read goes ahead as it did before this API existed.
const SETTLE_WAIT_MS = 8000;

// The named Firebase apps that can hold the customer-project session. There are TWO, signed in by two
// different paths in CustomerdatabaseService.connectDB, and which one runs depends on the branch:
//   'CustomerDatabase' - customerdatabase.service.ts:792, firebase.app()/initializeApp by that name
//   'app2'             - firebase.service.ts:74 initSecondApp, signed in at customerdatabase:694
// Both are signed in with the SAME custom token for the same customer project, so either ID token is
// equally valid. Looking at only one of them is why this silently produced no token at all.
//
// The DEFAULT app is deliberately excluded: it is the PLATFORM project (mona-ai-pwa-firebase), whose
// token carries the wrong audience and comes back 401. customerdatabase.service.ts says the same
// where it signs in - "to avoid default-app audience mismatch".
const CUSTOMER_APPS = ['CustomerDatabase', 'app2'];

export interface MemberGrant {
  uid: string;
  roles: PermissionsRoles;
  status: 'active' | 'suspended';
  source: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AuditEntry {
  id: string;
  at: string;
  action: 'create' | 'update' | 'suspend' | 'reactivate' | 'mint';
  actor_uid: string;
  target_uid: string;
  before: { roles: PermissionsRoles; status: string } | null;
  after: { roles: PermissionsRoles; status: string };
  sheet_revision: number;
}

// What a PUT carries. TWO fields, and `status` is REQUIRED on every call - not optional, even when
// nothing about the status is changing.
//
// `roles` names only the slots being changed (an explicit null removes that role); every slot left
// out is untouched. `status` must be the value the grant ALREADY has unless the caller holds
// account-level rights: an organigram-scoped administrator may not change it, and sending a
// different one comes back "you may not change this grant's status; that is account-wide". So the
// caller echoes grant.status back rather than assuming 'active'.
export interface GrantPatch {
  roles?: Partial<PermissionsRoles>;
  status: 'active' | 'suspended';
}

export interface AuditPage {
  entries: AuditEntry[];
  next_cursor: string | null;
}

// The client for the organigram's role-based-access API. Holds ONE piece of state - the current user's
// own permissions - and caches it in memory for the session; every response carries Cache-Control:
// no-store, so nothing is persisted anywhere.
//
// Deliberately not part of OrganigramApiService: that service is the client for the organigram REST
// backend and nothing else, and this speaks a different host and a different auth model (a real
// Firebase ID token in an Authorization header, where the REST backend takes X-Account-Id/X-User-Id and
// the callable receivers take ids in the body). It registers no HttpInterceptor for the same reason -
// a global one would attach this token to Microsoft Graph, BookStack and api.mona-ai.cloud as well.
@Injectable({ providedIn: 'root' })
export class OrganigramPermissionsService {
  private readonly load = new BehaviorSubject<PermissionsLoad>(UNKNOWN_PERMISSIONS);
  // A role MONA staff preview the organigram as. Session memory only, and it can only narrow.
  private readonly preview = new BehaviorSubject<string | null>(null);
  readonly load$ = combineLatest([this.load, this.preview]).pipe(map(([load, role]) => this.shown(load, role)));

  // The platform sign-in (default app), the same identity PricingConfigService checks. Any failure
  // counts as a customer, which is the stricter answer.
  isMonaStaff: () => boolean = () => {
    try {
      return isMonaAdminEmail(firebase.auth().currentUser?.email);
    } catch {
      return false;
    }
  };
  isFullAccessCustomer: () => boolean = () => {
    try {
      return isFullAccessCustomerEmail(firebase.auth().currentUser?.email);
    } catch {
      return false;
    }
  };
  private inFlight: Promise<void> | null = null;
  // /me/permissions has really been asked and has come back, whatever it said. "Could not ask yet" (no
  // customer DB, no token) does not count - see settled().
  private readonly answered = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private customerDB: CustomerdatabaseService,
    private organigramApi: OrganigramApiService
  ) {
    // Hydrate on the customer-DB auth signal rather than at construction: the named app is signed in
    // with the custom token inside that same block, just before the signal fires, so there is a token
    // to send by the time this runs. Unlike the chat dock this does NOT need to wait for the user id -
    // identity comes from the token, never from a header or a body field.
    this.customerDB.isAuthenticatedOnCustDb$.pipe(filter(Boolean)).subscribe(() => { this.refresh(); });
  }

  get current(): PermissionsLoad {
    return this.shown(this.load.value, this.preview.value);
  }

  private shown(load: PermissionsLoad, role: string | null): PermissionsLoad {
    return customerSafePermissions(
      previewPermissions(withDefaultViewer(load), role), this.isMonaStaff() || this.isFullAccessCustomer()
    );
  }

  get previewRole(): string | null {
    return this.preview.value;
  }

  setPreviewRole(role: string | null): void {
    const next = role && ORGANIGRAM_ROLE_EFFECTIVE[role] ? role : null;
    if (next !== this.preview.value) { this.preview.next(next); }
  }

  // The uid the grants API sees, taken from the SAME session the bearer token comes from. Deliberately
  // not OrganigramApiService.resolveUserId(): that one answers for the organigram REST backend's
  // X-User-Id header and can resolve to a different value, and a mismatch here would mark the wrong
  // row as "you". '' until the customer project has signed in.
  get myUid(): string {
    const user = this.customerUser();
    return user ? user.uid : '';
  }

  // Load /me/permissions into the cache. Safe to call repeatedly - concurrent calls share one request.
  refresh(): Promise<void> {
    if (this.inFlight) { return this.inFlight; }
    this.inFlight = this.fetchPermissions().then(
      next => {
        if (next) { this.load.next(next); this.answered.next(true); }
        else { this.load.next(UNKNOWN_PERMISSIONS); }   // could not ask yet - the auth signal asks again
      },
      () => { /* fetchPermissions never rejects; it maps every outcome to a state */ }
    ).then(() => { this.inFlight = null; });
    return this.inFlight;
  }

  // Resolves once /me/permissions has really answered - allowed, refused or failed - asking now if it
  // can. For reads that must not show anything before a refusal could land (the chat dock). Capped at
  // SETTLE_WAIT_MS: a request that stalls leaves those reads as they were before permissions existed
  // (unknown), rather than holding them forever.
  settled(): Promise<void> {
    if (this.answered.value) { return Promise.resolve(); }
    if (!this.inFlight) { this.refresh(); }
    return new Promise<void>(resolve => {
      const timer = setTimeout(() => { sub.unsubscribe(); resolve(); }, SETTLE_WAIT_MS);
      const sub = this.answered.pipe(filter(Boolean), take(1)).subscribe(() => { clearTimeout(timer); resolve(); });
    });
  }

  // Drop the cache back to unknown. Call on sign-out; on a grant change prefer refresh(), which
  // replaces the value without a window where everything is briefly allowed again.
  clear(): void {
    this.answered.next(false);
    this.preview.next(null);
    this.load.next(UNKNOWN_PERMISSIONS);
  }

  // null when the request could not be MADE (no customer DB yet, no token): nothing was asked, so it is
  // no answer. Anything that reached the network maps to a state.
  private async fetchPermissions(): Promise<PermissionsLoad | null> {
    const base = this.baseUrl();
    if (!base) { return null; }
    try {
      const body: any = await this.get(`${base}/me/permissions`);
      const roles = body && body.roles ? body.roles : {};
      return {
        state: 'loaded',
        effective: Array.isArray(body?.effective) ? body.effective : [],
        status: (body?.status || 'ok') as PermissionsStatus,
        roles: {
          account: roles.account ?? null,
          organigram: roles.organigram ?? null,
          jobs: roles.jobs ?? null,
          agents: roles.agents ?? null,
        },
      };
    } catch (err: any) {
      // No token yet: request() throws before anything is sent, with no HTTP status.
      if (err && err.status === undefined) { return null; }
      // Only a real refusal takes anything away. A 5xx, a timeout or an unreachable host stays
      // 'unknown', which renders exactly as the app did before this API existed - see PermissionsLoad.
      return isRefusal(err) ? { state: 'refused' } : UNKNOWN_PERMISSIONS;
    }
  }

  // ---- Members screen --------------------------------------------------------------------------
  // Both of these are gated by ROLE, not by `effective`: probe the endpoint and hide the screen on a
  // 403. There is no flag in /me/permissions that answers it, so do not try to infer one from the
  // account slot - the server decides.

  async listGrants(): Promise<MemberGrant[]> {
    const body: any = await this.get(`${this.requireBase()}/accounts/${ACCOUNT_SEGMENT}/grants`);
    return Array.isArray(body?.grants) ? body.grants : [];
  }

  // A PARTIAL update since the backend change of 2026-09-21: send only what is changing. An omitted
  // slot is LEFT ALONE - which is not the same as sending null, and the difference is load-bearing:
  //
  //   roles: { organigram: 'org_editor' }   -> sets that slot, account/jobs/agents untouched
  //   roles: { organigram: null }           -> REMOVES that role
  //   any slot left out                     -> untouched
  //
  // `status` goes on EVERY call. Echo the grant's current value unless the caller may actually change
  // it - see GrantPatch.
  //
  // It used to replace the whole grant, which is why callers had to read the other three slots and
  // send them back or wipe them. That is no longer true, and no longer done.
  //
  // Send it and render the answer - never pre-compute who may set what. The server's 403 and 409 texts
  // ("your role does not carry grant administration", "this would remove the last owner") are written
  // for humans and are shown verbatim by accessErrorMessage.
  async putGrant(targetUid: string, patch: GrantPatch): Promise<MemberGrant> {
    const url = `${this.requireBase()}/accounts/${ACCOUNT_SEGMENT}/grants/${encodeURIComponent(targetUid)}`;
    return await this.send('PUT', url, patch) as MemberGrant;
  }

  async listAudit(params: { limit?: number; target?: string; cursor?: string } = {}): Promise<AuditPage> {
    const query: string[] = [];
    if (params.limit) { query.push('limit=' + params.limit); }
    if (params.target) { query.push('target=' + encodeURIComponent(params.target)); }
    if (params.cursor) { query.push('cursor=' + encodeURIComponent(params.cursor)); }
    const suffix = query.length ? '?' + query.join('&') : '';
    const body: any = await this.get(`${this.requireBase()}/accounts/${ACCOUNT_SEGMENT}/audit${suffix}`);
    return {
      entries: Array.isArray(body?.entries) ? body.entries : [],
      next_cursor: body?.next_cursor ?? null,
    };
  }

  // ---- Plumbing --------------------------------------------------------------------------------

  // Where the API lives: the customer project's cloud-function host, derived from the connected
  // database rather than hardcoded, so it follows whichever customer project is signed in. Empty until
  // the customer DB has connected, which every caller treats as "cannot ask yet".
  //
  // On localhost only, localStorage['organigramAccessApiBase'] can point it elsewhere (e.g. a locally
  // served instance). Host-locked on purpose, like the dev_simulate_location flag the recruiting pages
  // use: an overridable API base is somewhere a bearer token gets sent, so a deployed build never reads it.
  private baseUrl(): string {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      const override = (localStorage.getItem('organigramAccessApiBase') || '').trim();
      if (override) { return override.replace(/\/+$/, ''); }
    }
    const host = webhookReceiverBase(this.organigramApi.projectId);
    return host ? `${host}/${ACCESS_FN}` : '';
  }

  private requireBase(): string {
    const base = this.baseUrl();
    if (!base) { throw new Error('The customer database has not connected yet. Reload and try again.'); }
    return base;
  }

  // A fresh ID token from the CUSTOMER project's named app. getIdToken() refreshes on its own when the
  // token is close to expiry; `force` re-mints it, which is the one retry a 401 earns.
  private async idToken(force = false): Promise<string> {
    const user = this.customerUser();
    if (!user) { return ''; }
    try {
      return await user.getIdToken(force);
    } catch {
      return '';
    }
  }

  // The signed-in customer-project user, from whichever named app actually holds the session. Falls
  // back to scanning every initialised app so a third sign-in path added later still works - the only
  // hard rule is that the DEFAULT app is never used, because it is the platform project.
  private customerUser(): firebase.User | null {
    for (const name of CUSTOMER_APPS) {
      try {
        const user = firebase.app(name).auth().currentUser;
        if (user) { return user; }
      } catch {
        // firebase.app() throws when that app was never initialised. Try the next one.
      }
    }
    try {
      for (const app of firebase.apps) {
        if (!app || app.name === '[DEFAULT]') { continue; }
        const user = app.auth().currentUser;
        if (user) { return user; }
      }
    } catch {
      // Auth not available on an app; nothing else to try.
    }
    return null;
  }

  private get(url: string): Promise<any> {
    return this.send('GET', url);
  }

  // One retry on 401 with a force-refreshed token, then the error stands. The API treats 401 as
  // "missing, expired or revoked": a token this client minted seconds ago is none of those, so the
  // retry separates a genuinely revoked session from a token that simply aged out mid-session.
  private async send(method: 'GET' | 'PUT', url: string, body?: any): Promise<any> {
    try {
      return await this.request(method, url, body, false);
    } catch (err) {
      if (Number((err as any)?.status) === 401) { return await this.request(method, url, body, true); }
      throw err;
    }
  }

  private async request(method: 'GET' | 'PUT', url: string, body: any, force: boolean): Promise<any> {
    const token = await this.idToken(force);
    if (!token) {
      throw new Error('Not signed in to the customer project yet. Reload the page and try again.');
    }
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    if (method === 'PUT') { headers = headers.set('Content-Type', 'application/json'); }
    return method === 'GET'
      ? await this.http.get(url, { headers }).toPromise()
      : await this.http.put(url, body, { headers }).toPromise();
  }
}

// Did the backend actually refuse, or did the call just not get through? Only the first takes UI away.
export function isRefusal(err: any): boolean {
  const status = Number(err?.status);
  return status === 401 || status === 403;
}

// The server's error text, verbatim. Every error body is { "error": "<message>" } and those messages
// are written for humans ("you cannot appoint your own level", "this would remove the last owner") -
// so show them rather than mapping the status to wording of our own, which is what the rest of the
// organigram does only because its backends send no usable text.
export function accessErrorMessage(err: any): string {
  const text = err?.error?.error;
  if (typeof text === 'string' && text.trim()) { return text; }
  const status = Number(err?.status);
  if (status === 403) { return 'You are not allowed to do that.'; }
  if (status === 401) { return 'Your session has expired. Sign in again.'; }
  if (!status) { return 'The request did not reach the backend. Check the connection and try again.'; }
  return 'The backend answered ' + status + '.';
}
